import { normalizeArrayResponse } from "@/data/source/response-normalizer";
import { URLList } from "@/data/url_list";
import { createLogger, withApiLogging } from "@/utils/logger/logger";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";

export class PrescriptionSource {
  private readonly logger = createLogger("PrescriptionSource");

  constructor(private readonly client: FetchApiClientMethod) {}

  async fetchPrescriptionList(patientUuid: string): Promise<SATResult<unknown[], SATError>> {
    const url = `${URLList.PRESCRIPTION.LIST}/${patientUuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(normalizeArrayResponse(data, PRESCRIPTION_LIST_RESPONSE_KEYS));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchPrescriptionDetail(
    patientUuid: string,
    prescriptionUuid: string,
  ): Promise<SATResult<unknown, SATError>> {
    const url = `${URLList.TREATMENT.DETAIL}/${patientUuid}/${prescriptionUuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchActivityList(prescriptionUuid: string): Promise<SATResult<unknown, SATError>> {
    const url = `${URLList.PRESCRIPTION.ACTIVITY_LIST}/${prescriptionUuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchActivityDetail(activityUuid: string): Promise<SATResult<unknown, SATError>> {
    const url = `${URLList.PRESCRIPTION.ACTIVITY_DETAIL}/${activityUuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchEyeActivityData(activityUuid: string): Promise<SATResult<unknown, SATError>> {
    const url = `${URLList.PRESCRIPTION.EYE_CHART}/${activityUuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async createPrescription(payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const url = URLList.PRESCRIPTION.CREATE;
    try {
      await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async cancelPrescription(
    patientUuid: string,
    prescriptionUuid: string,
  ): Promise<SATResult<void, SATError>> {
    const url = `${URLList.PRESCRIPTION.CANCEL}/${patientUuid}/${prescriptionUuid}`;
    try {
      await withApiLogging(this.logger, { method: "DELETE", url }, async () => {
        const response = await this.client.delete(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async sendPatientMessage(
    patientUuid: string,
    title: string,
    content: string,
  ): Promise<SATResult<void, SATError>> {
    const url = `${URLList.PRESCRIPTION.SEND_MESSAGE}/${patientUuid}`;
    const payload = { title, content };
    try {
      await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async submitActivityFeedback(
    activityUuid: string,
    comment: string,
  ): Promise<SATResult<void, SATError>> {
    const url = `${URLList.PRESCRIPTION.SUBMIT_ACTIVITY_FEEDBACK}/${activityUuid}`;
    const payload = { comment };
    try {
      await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

}

const PRESCRIPTION_LIST_RESPONSE_KEYS = [
  "prescriptionList",
  "prescriptions",
  "dataList",
  "items",
  "list",
  "result",
  "content",
  "data",
] as const;
